import { getMCPMonitorSentimentDistribution } from "@/api/endpoints/searches";
import { SENTIMENT, SENTIMENT_TO_COLOR, SENTIMENT_TO_LABEL } from "@/constants";
import { useI18n } from "@/contexts/i18nContext";
import { cn } from "@/lib/utils";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/tooltip";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetTitle,
} from "@components/ui/widget";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import {
  getMCPMonitorFiltersFromSearchFilterV4,
  getOverallSentiment,
} from "./utils";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "lucide-react";
import { CardTitle } from "@components/ui/card";

export default function SentimentDistributionWidget({
  detailsLink,
  searchUuid,
  className,
  filters,
  ...rest
}: {
  filters: SearchFilterV4WithDates;
  detailsLink?: string;
  searchUuid: Uuid;
} & WidgetProps) {
  const { formatPercent } = useI18n();

  // Server state
  const {
    data: sentimentDistribution,
    isLoading: isSentimentDistributionLoading,
  } = useQuery({
    queryKey: [searchUuid, "sentiment-distribution", filters],
    queryFn: () =>
      getMCPMonitorSentimentDistribution(
        searchUuid,
        getMCPMonitorFiltersFromSearchFilterV4(filters)
      ),
  });

  const shares = sentimentDistribution?.ok
    ? sentimentDistribution.data.shares
    : undefined;

  const overallSentiment = shares ? getOverallSentiment(shares) : undefined;

  // Helpers
  const noSentimentValues = shares && !Object.values(shares).some(Boolean);

  return (
    <Widget className={cn("h-32", className)} {...rest}>
      <WidgetHeader
        isLoading={isSentimentDistributionLoading}
        skeletonClassName="h-7"
      >
        {detailsLink ? (
          <Link
            to={detailsLink}
            className="flex items-center gap-1.5 group/link flex-1"
          >
            <WidgetTitle className="group-hover/link:underline group-hover/link:underline-offset-4">
              <Trans>Övergripande sentiment</Trans>
            </WidgetTitle>
            <ArrowRightIcon className="size-4 opacity-0 group-hover/link:opacity-100 -translate-x-2 group-hover/link:translate-x-0 transition-all" />
          </Link>
        ) : (
          <WidgetTitle>
            <Trans>Övergripande sentiment</Trans>
          </WidgetTitle>
        )}

        {overallSentiment && (
          <CardTitle className="text-xl font-semibold">
            {i18n._(SENTIMENT_TO_LABEL[overallSentiment])}
          </CardTitle>
        )}
      </WidgetHeader>
      <WidgetContent
        className="justify-end"
        isLoading={isSentimentDistributionLoading}
        isEmpty={
          !isSentimentDistributionLoading &&
          (!sentimentDistribution?.ok || noSentimentValues)
        }
      >
        {shares ? (
          <div
            className={cn(
              "flex h-6 rounded-sm",
              noSentimentValues &&
                "divide-x divide-dashed divide-muted-foreground border border-dashed border-muted-foreground"
            )}
          >
            {Object.values(SENTIMENT).map((key, i) => {
              const share = shares[key];
              const isFirst = !i;
              const isLast = i === Object.values(SENTIMENT).length - 1;
              const hasShare = share > 0;

              return (
                <Tooltip key={key}>
                  <TooltipTrigger
                    className={cn(
                      "cursor-default min-w-3 px-0.5",
                      isFirst && "pl-0 rounded-l-sm",
                      isLast && "pr-0 rounded-r-sm",
                      noSentimentValues && "flex-1",
                      !hasShare && !noSentimentValues && "stripes mx-px"
                    )}
                    style={{
                      width: noSentimentValues
                        ? undefined
                        : `${Math.round(share * 100)}%`,
                      backgroundColor: hasShare
                        ? SENTIMENT_TO_COLOR[key]
                        : undefined,
                    }}
                  />

                  <TooltipContent>
                    {i18n._(SENTIMENT_TO_LABEL[key])}:{" "}
                    {formatPercent(shares[key], 0)}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        ) : null}
      </WidgetContent>
    </Widget>
  );
}
